import { SagaIterator } from 'redux-saga';
import { call, delay, put, select } from 'redux-saga/effects';
import { makeReqWithRD, TMakeReqWithRD } from 'redux/makeReqWithRD';
import { genFetchedData } from 'redux/fetchedData';
import updateToken from 'utils/updateToken';
import {
  checkDynamicCatalogData,
  getObjectionNextStep,
  getReport,
  getReportSettings,
  getReportsList,
} from '../model/actions';
import {
  checkDynamicCatalogDataAPI,
  getObjectionNextStepAPI,
  getReportAPI,
  getReportSettingsAPI,
  getReportsListAPI,
} from '../api';
import {
  setNextStepData,
  setNotExistInCatalogKeys,
  setReportByFilter,
  setReportSettings,
  setReportsList,
} from '../reducer';
import { selectorNextStepData } from '../model/selectors';
import {
  TCheckDynamicCatalogDataResult,
  TGetObjectionNextStepData,
  TGetReportData,
} from '../types';

export function* fetchReportListWorker({
  payload,
}: ReturnType<typeof getReportsList>): SagaIterator<void> {
  try {
    yield call<TMakeReqWithRD<typeof getReportsListAPI>>(makeReqWithRD, {
      fetcher: getReportsListAPI,
      fill: setReportsList,
      parameters: payload,
    });
  } catch (e: unknown) {
    console.error(e);
  }
}

export function* fetchObjectionNextStepWorker({
  payload,
}: ReturnType<typeof getObjectionNextStep>): SagaIterator<void> {
  let fetchedData = yield select(selectorNextStepData);
  fetchedData = fetchedData.set(
    'isLoading',
    true,
  );
  yield put(setNextStepData(fetchedData));

  try {
    yield call(updateToken);

    const response: TGetObjectionNextStepData = yield call(
      getObjectionNextStepAPI,
      payload,
    );

    if (response) {
      fetchedData = fetchedData.set('data', response);
      yield put(setNextStepData(fetchedData));
    }
  } catch (e: unknown) {
    console.error(e);
    fetchedData = fetchedData.set('error', {
      isError: true,
      message: e.message ?? '',
      code: e.code ?? '',
      fields: e.data.fields,
    });
    yield put(setNextStepData(fetchedData));
  } finally {
    fetchedData = fetchedData.set('isLoading', false);
    yield put(setNextStepData(fetchedData));
  }
}

export function* fetchReportSettingsWorker({
  payload,
}: ReturnType<typeof getReportSettings>): SagaIterator<void> {
  try {
    yield call<TMakeReqWithRD<typeof getReportSettingsAPI>>(makeReqWithRD, {
      fetcher: getReportSettingsAPI,
      fill: setReportSettings,
      parameters: payload,
    });
  } catch (e: unknown) {
    console.error(e);
  }
}

export function* fetchcheckDynamicCatalogDataWorker({
  payload,
}: ReturnType<typeof checkDynamicCatalogData>): SagaIterator<void> {
  let fetchedData = genFetchedData<
    Record<string, TCheckDynamicCatalogDataResult>
  >(null).set('isLoading', true);
  yield put(setNotExistInCatalogKeys(fetchedData));
  const { currentKey, type, keys } = payload;
  try {
    yield call(updateToken);
    const response: TCheckDynamicCatalogDataResult = yield call(
      checkDynamicCatalogDataAPI,
      { type, keys },
    );

    if (response) {
      const formatedResponse = {
        [currentKey]: response,
      };

      fetchedData = fetchedData.set('data', formatedResponse);
      yield put(setNotExistInCatalogKeys(fetchedData));
    }
  } catch (e: unknown) {
    console.error(e);
    fetchedData = fetchedData.set('error', {
      isError: true,
      message: e.message ?? '',
      code: e.code ?? '',
      fields: e.data.fields,
    });
    yield put(setNotExistInCatalogKeys(fetchedData));
  } finally {
    fetchedData = fetchedData.set('isLoading', false);
    yield put(setNotExistInCatalogKeys(fetchedData));

    yield delay(3000);
    
    // Очищаем ошибки
    const clearedData = fetchedData.set('data', null);
    yield put(setNotExistInCatalogKeys(clearedData));
  }
}

export function* getReportByFilterWorker({
  payload,
}: ReturnType<typeof getReport>): SagaIterator<void> {
  let fetchedData = genFetchedData<TGetReportData>(null).set(
    'isLoading',
    true,
  );
  yield put(setReportByFilter(fetchedData));

  try {
    yield call(updateToken);
    const response: TGetReportData = yield call(getReportAPI, payload);

    if (response) {
      fetchedData = fetchedData.set('data', response);
      yield put(setReportByFilter(fetchedData));
    }
  } catch (e: unknown) {
    const error = e as {
      message?: string;
      code?: number | string;
      data?: {
        fields?: string[];
      };
    };

    console.error(e);
    fetchedData = fetchedData.set('error', {
      isError: true,
      message: error.message ?? '',
      code: Number(error.code) || 0,
      fields: error.data?.fields,
    });
    yield put(setReportByFilter(fetchedData));
  } finally {
    fetchedData = fetchedData.set('isLoading', false);
    yield put(setReportByFilter(fetchedData));
  }
}
